import { useLayoutEffect, type RefObject } from 'react';

interface Options {
  enabled?: boolean;
  box?: ResizeObserverBoxOptions;
  /** Remeasure when content changes without changing the observed box (e.g. clamped text). */
  remeasureKey?: unknown;
}

/** Measure once before paint, then coalesce resize notifications into the next frame.
 * Layout-affecting updates must not run during ResizeObserver delivery itself.
 * Pass a stable measure callback; it owns any value comparison or external-style cleanup.
 */
export function useRafResizeObserver<T extends Element>(
  ref: RefObject<T>,
  measure: (element: T) => void,
  { enabled = true, box = 'content-box', remeasureKey }: Options = {},
) {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;
    let frame: number | undefined;
    measure(element);
    const observer = new ResizeObserver(() => {
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        measure(element);
      });
    });
    observer.observe(element, { box });
    return () => {
      observer.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [ref, measure, enabled, box, remeasureKey]);
}
